import { Suspense, lazy, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import "@/App.css";
import "@/styles/jump-template.css";
import AuthPage from "@/pages/AuthPage";
import AuthSetupPage from "@/pages/AuthSetupPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ProjectPage from "@/pages/ProjectPage";
import FullTasksPage from "@/pages/FullTasksPage";
import ProjectsPage from "@/pages/ProjectsPage";
import BriefBuilderPage from "@/pages/BriefBuilderPage";
import ProfilePage from "@/pages/ProfilePage";
import ProfileLeavesPage from "@/pages/ProfileLeavesPage";
import ProfileStatsPage from "@/pages/ProfileStatsPage";
import ProfileNotificationsPage from "@/pages/ProfileNotificationsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import StandaloneHtmlFramePage from "@/pages/StandaloneHtmlFramePage";
import { ShellCacheProvider } from "@/context/ShellCacheContext";
import { Toaster } from "@/components/ui/sonner";
import { resolveOrgLevel } from "@/lib/org";

const LearningPage = lazy(() => import("@/pages/LearningPage"));
const HRPage = lazy(() => import("@/pages/HRPage"));
const OrgEditorPage = lazy(() => import("@/pages/OrgEditorPage"));
const ManagerDashboardPage = lazy(() => import("@/pages/ManagerDashboardPage"));
const FinanceReceivablesPage = lazy(() => import("@/pages/FinanceReceivablesPage"));
const LeaderboardPage = lazy(() => import("@/pages/LeaderboardPage"));
const PodDetailPage = lazy(() => import("@/pages/PodDetailPage"));
const WorkshopsPage = lazy(() => import("@/pages/WorkshopsPage"));
const ClientPortalOverviewPage = lazy(() => import("@/pages/client-portal/ClientPortalOverviewPage"));
const ClientPortalProjectPage = lazy(() => import("@/pages/client-portal/ClientPortalProjectPage"));
const ClientPortalTasksPage = lazy(() => import("@/pages/client-portal/ClientPortalTasksPage"));
const ClientPortalTeamPage = lazy(() => import("@/pages/client-portal/ClientPortalTeamPage"));
const ClientPortalBillingPage = lazy(() => import("@/pages/client-portal/ClientPortalBillingPage"));
const ProjectUpdateClientViewPage = lazy(() => import("@/pages/ProjectUpdateClientViewPage"));
const AuditLogsPage = lazy(() => import("@/pages/AuditLogsPage"));
const KPITrackerPage = lazy(() => import("@/pages/KPITrackerPage"));
const MyKPITargetsPage = lazy(() => import("@/pages/MyKPITargetsPage"));
const ContractsRenewalsPage = lazy(() => import("@/pages/ContractsRenewalsPage"));
const ManagerGoalsPage = lazy(() => import("@/pages/ManagerGoalsPage"));

const SESSION_STORAGE_KEY = "client-value-tracker-session";

const readSessionValue = () => {
  try {
    const sessionRaw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionRaw) return sessionRaw;
    const legacyRaw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (legacyRaw) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, legacyRaw);
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      return legacyRaw;
    }
  } catch (_error) {
    // no-op
  }
  return "";
};

const persistSessionValue = (value) => {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, value);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (_error) {
    // no-op
  }
};

const clearSessionValue = () => {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (_error) {
    // no-op
  }
};

const getStoredSession = () => {
  const raw = readSessionValue();
  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch (_error) {
    clearSessionValue();
    return null;
  }
};

function App() {
  const [session, setSession] = useState(getStoredSession);
  const user = session?.user ?? null;
  const requiresSetup = Boolean(user && user.profile_complete === false);

  const authHandlers = useMemo(
    () => ({
      onAuthSuccess: (authenticatedSession) => {
        setSession(authenticatedSession);
        persistSessionValue(JSON.stringify(authenticatedSession));
      },
      onLogout: () => {
        setSession(null);
        clearSessionValue();
      },
      onSessionUpdate: (nextUser) => {
        setSession((prev) => {
          if (!prev?.user) return prev;
          const nextSession = {
            ...prev,
            user: {
              ...prev.user,
              ...nextUser,
            },
          };
          persistSessionValue(JSON.stringify(nextSession));
          return nextSession;
        });
      },
    }),
    [],
  );

  const lazyWrap = (node) => (
    <Suspense fallback={<div className="jump-card p-4 text-sm text-slate-500" data-testid="route-lazy-loading-state">Loading page...</div>}>
      {node}
    </Suspense>
  );

  return (
    <div className="app-root" data-testid="app-root">
      <ShellCacheProvider>
        <BrowserRouter>
        <Routes>
          <Route
            path="/client/a-new-way-to-work"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-client.html"
                title="Client Brief"
                seo={{
                  title: "A New Way to Work — Client Brief | Dimerse",
                  description: "Client briefing page for A New Way to Work by Dimerse.",
                  canonicalPath: "/client/a-new-way-to-work/",
                }}
                rootTestId="brief-client-page-root"
                frameTestId="brief-client-page-iframe"
              />
            }
          />
          <Route
            path="/client/a-new-way-to-work/"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-client.html"
                title="Client Brief"
                seo={{
                  title: "A New Way to Work — Client Brief | Dimerse",
                  description: "Client briefing page for A New Way to Work by Dimerse.",
                  canonicalPath: "/client/a-new-way-to-work/",
                }}
                rootTestId="brief-client-page-root"
                frameTestId="brief-client-page-iframe"
              />
            }
          />
          <Route
            path="/employee/a-new-way-to-work"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-execution.html"
                title="Execution Brief"
                seo={{
                  title: "A New Way to Work — Execution Brief | Dimerse",
                  description: "Execution briefing page for Dimerse employees.",
                  canonicalPath: "/employee/a-new-way-to-work/",
                }}
                rootTestId="brief-execution-page-root"
                frameTestId="brief-execution-page-iframe"
              />
            }
          />
          <Route
            path="/employee/a-new-way-to-work/"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-execution.html"
                title="Execution Brief"
                seo={{
                  title: "A New Way to Work — Execution Brief | Dimerse",
                  description: "Execution briefing page for Dimerse employees.",
                  canonicalPath: "/employee/a-new-way-to-work/",
                }}
                rootTestId="brief-execution-page-root"
                frameTestId="brief-execution-page-iframe"
              />
            }
          />
          <Route
            path="/pod-owner/a-new-way-to-work"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-pod-owner.html"
                title="Pod Owner Brief"
                seo={{
                  title: "A New Way to Work — POD Owner Brief | Dimerse",
                  description: "POD owner briefing page for A New Way to Work.",
                  canonicalPath: "/pod-owner/a-new-way-to-work/",
                }}
                rootTestId="brief-pod-owner-page-root"
                frameTestId="brief-pod-owner-page-iframe"
              />
            }
          />
          <Route
            path="/pod-owner/a-new-way-to-work/"
            element={
              <StandaloneHtmlFramePage
                source="/briefs/brief-pod-owner.html"
                title="Pod Owner Brief"
                seo={{
                  title: "A New Way to Work — POD Owner Brief | Dimerse",
                  description: "POD owner briefing page for A New Way to Work.",
                  canonicalPath: "/pod-owner/a-new-way-to-work/",
                }}
                rootTestId="brief-pod-owner-page-root"
                frameTestId="brief-pod-owner-page-iframe"
              />
            }
          />

          <Route
            path="/auth"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthPage onAuthSuccess={authHandlers.onAuthSuccess} />
              )
            }
          />

          <Route path="/client/:clientSlug" element={lazyWrap(<ClientPortalOverviewPage />)} />
          <Route path="/client/:clientSlug/overview" element={lazyWrap(<ClientPortalOverviewPage />)} />
          <Route path="/client/:clientSlug/projects" element={lazyWrap(<ClientPortalProjectPage />)} />
          <Route path="/client/:clientSlug/project/:projectId" element={lazyWrap(<ClientPortalProjectPage />)} />
          <Route path="/client/:clientSlug/tasks" element={lazyWrap(<ClientPortalTasksPage />)} />
          <Route path="/client/:clientSlug/team" element={lazyWrap(<ClientPortalTeamPage />)} />
          <Route path="/client/:clientSlug/billing" element={lazyWrap(<ClientPortalBillingPage />)} />

          <Route
            path="/auth/setup"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : !requiresSetup ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <AuthSetupPage user={user} onAuthSuccess={authHandlers.onAuthSuccess} />
              )
            }
          />

          <Route
            path="/dashboard"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <DashboardPage user={user} onLogout={authHandlers.onLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/clients"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ClientsPage user={user} onLogout={authHandlers.onLogout} />
              )
            }
          />

          <Route
            path="/projects"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProjectsPage user={user} onLogout={authHandlers.onLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/project/:projectId"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProjectPage user={user} onLogout={authHandlers.onLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/projects/:projectId"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProjectPage user={user} onLogout={authHandlers.onLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/project/:clientSlug/update-client-view"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<ProjectUpdateClientViewPage user={user} onLogout={authHandlers.onLogout} />)
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/tasks"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <FullTasksPage user={user} onLogout={authHandlers.onLogout} />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/learning"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<LearningPage user={user} onLogout={authHandlers.onLogout} />)
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/hr"
            element={
              user ? requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<HRPage user={user} onLogout={authHandlers.onLogout} />)
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />

          <Route
            path="/profile"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProfilePage user={user} onLogout={authHandlers.onLogout} onSessionUpdate={authHandlers.onSessionUpdate} />
              )
            }
          />

          <Route
            path="/profile/leaves"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProfileLeavesPage user={user} onLogout={authHandlers.onLogout} />
              )
            }
          />

          <Route
            path="/profile/stats"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProfileStatsPage user={user} onLogout={authHandlers.onLogout} />
              )
            }
          />

          <Route
            path="/profile/notifications"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <ProfileNotificationsPage user={user} onLogout={authHandlers.onLogout} onSessionUpdate={authHandlers.onSessionUpdate} />
              )
            }
          />

          <Route
            path="/notifications"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                <NotificationsPage user={user} onLogout={authHandlers.onLogout} />
              )
            }
          />

          <Route
            path="/audit-logs"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : user.role !== "super_admin" ? (
                <Navigate to="/dashboard" replace />
              ) : (
                lazyWrap(<AuditLogsPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/org-editor"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<OrgEditorPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/manager-dashboard"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<ManagerDashboardPage user={user} onLogout={authHandlers.onLogout} onSessionUpdate={authHandlers.onSessionUpdate} />)
              )
            }
          />

          <Route
            path="/finance"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : user.role !== "super_admin" ? (
                <Navigate to="/dashboard" replace />
              ) : (
                lazyWrap(<FinanceReceivablesPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/contracts-renewals"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : resolveOrgLevel(user) > 2 ? (
                <Navigate to="/dashboard" replace />
              ) : (
                lazyWrap(<ContractsRenewalsPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/manager-goals"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : resolveOrgLevel(user) > 2 ? (
                <Navigate to="/dashboard" replace />
              ) : (
                lazyWrap(<ManagerGoalsPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/kpi-tracker"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : resolveOrgLevel(user) > 2 ? (
                <Navigate to="/my-kpi-targets" replace />
              ) : (
                lazyWrap(<KPITrackerPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/my-kpi-targets"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : resolveOrgLevel(user) <= 2 ? (
                <Navigate to="/kpi-tracker" replace />
              ) : (
                lazyWrap(<MyKPITargetsPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route
            path="/leaderboard"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<LeaderboardPage user={user} onLogout={authHandlers.onLogout} />)
              )
            }
          />

          <Route path="/scorecard" element={<Navigate to="/manager-dashboard" replace />} />

          <Route
            path="/pod-detail/:managerEmail"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<PodDetailPage user={user} onLogout={authHandlers.onLogout} onSessionUpdate={authHandlers.onSessionUpdate} />)
              )
            }
          />

          <Route
            path="/workshops"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : (
                lazyWrap(<WorkshopsPage user={user} onLogout={authHandlers.onLogout} onSessionUpdate={authHandlers.onSessionUpdate} />)
              )
            }
          />

          <Route
            path="/brief"
            element={
              !user ? (
                <Navigate to="/auth" replace />
              ) : requiresSetup ? (
                <Navigate to="/auth/setup" replace />
              ) : !["super_admin", "project_admin", "account_manager"].includes(user.role) ? (
                <Navigate to="/dashboard" replace state={{ denied: true }} />
              ) : (
                <BriefBuilderPage user={user} onLogout={authHandlers.onLogout} />
              )
            }
          />

          <Route path="/brand-brief" element={<Navigate to="/brief" replace />} />

          <Route
            path="*"
            element={
              user ? (
                requiresSetup ? <Navigate to="/auth/setup" replace /> : <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </ShellCacheProvider>
    </div>
  );
}

export default App;
